// How a steam room's finish lies on its faces in 3D (docs/claude/woods.md, *Steam
// room finishes*): which faces are a wall panel's skins and which cut across it,
// and where a cut face's points sit across the panel. Pure and DOM-free: the 3D
// view hands it a mesh's points in the room's LOCAL frame (x, y in mm, z above
// the room's floor) and the normal of the face each one is on, as woodFaces.ts.

import type { Vec2 } from '../shared/types';
import type { P3 } from './woodFaces';
import { pointInPolygon } from './geometry';

/** How far off a face its side is probed (mm), as `faceSide`. */
const PROBE = 5;

/** A face that looks this far up or down is level (a top, a sill, a soffit). */
const LEVEL = 0.7;

/** A plan point's distance (mm) to a closed outline, and the outline's edge
 *  nearest it (from point `edge` to the next). */
export function outlineDistance(outer: readonly Vec2[], p: Vec2): { d: number; edge: number } {
  let best = Infinity, edge = 0;
  for (let i = 0; i < outer.length; i++) {
    const a = outer[i], b = outer[(i + 1) % outer.length];
    const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
    const d = Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
    if (d < best) { best = d; edge = i; }
  }
  return { d: best, edge };
}

/** What a wall face of a room built of sandwich panels `thickness` thick shows:
 *  its outside skin when a point a little off it along its normal is outside the
 *  room's outer outline; a CUT across the panel, its core between the skins, when
 *  that point is still within the wall (a jamb, which looks along the wall) or
 *  the face is level and lies within it (a header's soffit, a sill, a wall's
 *  top); else its inside skin — the inner faces and a partition's. */
export function panelFace(outer: readonly Vec2[], thickness: number, c: P3, n: P3): 'in' | 'out' | 'cut' {
  const within = (p: Vec2): boolean => outlineDistance(outer, p).d < thickness - 0.5;
  if (Math.abs(n[2]) > LEVEL) return within({ x: c[0], y: c[1] }) ? 'cut' : 'in';
  const l = Math.hypot(n[0], n[1]) || 1;
  const q = { x: c[0] + (n[0] / l) * PROBE, y: c[1] + (n[1] / l) * PROBE };
  if (!pointInPolygon(q, outer as Vec2[])) return 'out';
  return within(q) ? 'cut' : 'in';
}

/** A cut face's UVs, a pair per point of one triangle: u across the panel, from 0
 *  on its outside face to 1 on its inside (a point's distance in from the outer
 *  outline over the thickness), and v along the cut in units of `along` mm — up
 *  an upright face, along the wall nearest the triangle on a level one. */
export function cutUVs(outer: readonly Vec2[], thickness: number, tri: readonly P3[], n: P3, along: number): number[] {
  let dir: Vec2 | null = null;
  if (Math.abs(n[2]) > LEVEL) {
    const m = { x: (tri[0][0] + tri[1][0] + tri[2][0]) / 3, y: (tri[0][1] + tri[1][1] + tri[2][1]) / 3 };
    const e = outlineDistance(outer, m).edge, a = outer[e], b = outer[(e + 1) % outer.length];
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    dir = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
  }
  const out: number[] = [];
  for (const p of tri) {
    const u = thickness > 0 ? Math.max(0, Math.min(1, outlineDistance(outer, { x: p[0], y: p[1] }).d / thickness)) : 0;
    out.push(u, (dir ? p[0] * dir.x + p[1] * dir.y : p[2]) / along);
  }
  return out;
}
